//********* TUTORIALS *********//
import * as types from "../../actions/actionTypes";
import {getExportAIData, getActivePartIdsForFeatureArea} from "./aiFunctions";
import {getCurrentLayout} from "../appLayout";
import {setLocalStorageItem} from "../../misc/storageFunctions";
import {getCommonVisualizerType} from "../../config";
import {FEATURE_AREAS} from "./aiFeatureAreasConstants";
import {updateLocalStorage} from "../../misc/jobLocalStorageSave";

// aiRegenerateLoading marks the "regenerate with a custom prompt" round trip of the custom
// prompt block (see postRegenerateLookAiImage in aiApi/aiRegenerateApi.ts). It is a plain subset
// of aiLoading - used to disable that block's textarea/button and to show the loading bar over
// the image being edited. Every other caller passes a single argument, which resets it.
export const setAILoading = (aiLoading, aiRegenerateLoading = false) => ({
  type: types.SET_AI_LOADING,
  payload: {aiLoading, aiRegenerateLoading: !!aiLoading && !!aiRegenerateLoading}
});

export const setAIWaitingId = (aiWaitingId) => {
  setLocalStorageItem("aiWaitingId", aiWaitingId);
  return {
    type: types.SET_AI_WAITING_ID,
    payload: {aiWaitingId}
  }
}

export const setAIResults = (aiResults) => {
  return (dispatch) => {
    dispatch({
      type: types.SET_AI_RESULTS,
      payload: {aiResults}
    });
    updateLocalStorage();
  }
};

export const setLookAiImageOpened = (lookAiImageOpened) => ({
  type: types.SET_LOOK_AI_IMAGE_OPENED,
  payload: {lookAiImageOpened}
});

export const setAiUserFileBlobUrl = (aiUserFileBlobUrl) => ({
  type: types.SET_AI_USER_FILE_BLOB_URL,
  payload: {aiUserFileBlobUrl}
});

export const setLookAiRenderFinished = (lookAiRenderFinished) => ({
  type: types.SET_LOOK_AI_RENDER_FINIHSED,
  payload: {lookAiRenderFinished}
});

export const setLookAiWaitingBoxClicked = (lookAiWaitingBoxClicked) => ({
  type: types.SET_LOOK_AI_WAITING_BOX_CLICKED,
  payload: {lookAiWaitingBoxClicked}
});

export const setLookAiLimitBoxOpened = (lookAiLimitBoxOpened) => ({
  type: types.SET_LOOK_AI_LIMIT_BOX_OPENED,
  payload: {lookAiLimitBoxOpened}
});

export const setLookAiImageExpanded = (aiImageExpanded) => ({
  type: types.SET_LOOK_AI_IMAGE_EXPANDED,
  payload: {aiImageExpanded}
});

export const setLookAiExportData = (groupObjects, aiResults) => {
  return (dispatch, getState) => {
    const aiExportData = getExportAIData(getState().aiState, groupObjects, aiResults);
    dispatch({
      type: types.SET_LOOK_AI_EXPORT_DATA,
      payload: {aiExportData}
    })
  }
}

export const setLookAiImportData = (aiImportData) => {
  return (dispatch, getState) => {
    dispatch({
      type: types.SET_LOOK_AI_IMPORT_DATA,
      payload: {
        aiImportData,
        aiResults: aiImportData?.aiResults || getState().aiState.aiResults,
      }
    })
  }
}

export const setAiRenderOptions = (aiRenderOptions) => ({
  type: types.SET_AI_RENDER_OPTIONS,
  payload: aiRenderOptions
});

export const setAiFeatureDefinitionIds = (aiFeatureDefinitionIds) => ({
  type: types.SET_AI_FEATURE_DEFINITION_IDS,
  payload: {aiFeatureDefinitionIds}
});

export const setAiDocumentFeatures = (aiDocumentFeatures) => ({
  type: types.SET_AI_DOCUMENT_FEATURES,
  payload: {aiDocumentFeatures}
});

export const setAiFeatureAreas = (aiFeatureAreas) => ({
  type: types.SET_AI_FEATURE_AREAS,
  payload: aiFeatureAreas
});

export const setAiFeatureAreasUserModified = (aiFeatureAreasUserModified) => ({
  type: types.SET_AI_FEATURE_AREAS_USER_MODIFIED,
  payload: {aiFeatureAreasUserModified}
});

/**
 * Computes the default selected partIds for every AI feature area (Wet Area, Vanity Area)
 * based on what's currently active in the layout, and applies them - same logic as the
 * "select" branch of toggleFeatureArea in UiPopupAIContentFeaturesAreas.
 *
 * Only areas enabled in the global settings (aiAreasWetArea / aiAreasVanityArea, see
 * `settingKey` in FEATURE_AREAS) are checked by default - disabled ones are left empty.
 *
 * Called: (1) once getAIGlobalSettings first receives settings with aiFeatureAreaEnabled on,
 * and (2) every time UiPopupAIContentFeaturesAreas or LookAIPopupContent is opened, so the
 * defaults reflect whatever is currently on the canvas. Bails out on its own if the user has
 * already taken manual control (aiFeatureAreasUserModified), so callers don't need to guard it.
 */
export const setDefaultAiFeatureAreasGroups = () => {
  return (dispatch, getState) => {
    if (getCommonVisualizerType() !== "bathroom" || getState().aiState.aiFeatureAreasUserModified) {
      return;
    }
    const layout = getCurrentLayout();
    const aiRenderOptions = getState().aiState.aiRenderOptions;
    const aiFeatureAreas = FEATURE_AREAS.reduce((result, {featureArea, partIds, settingKey}) => {
      if (!aiRenderOptions[settingKey]) {
        result[featureArea] = [];
        return result;
      }
      const availablePartIds = partIds.filter(partId => layout?.getFeatureGroupByPartId(partId));
      result[featureArea] = getActivePartIdsForFeatureArea(availablePartIds, layout);
      return result;
    }, {});
    dispatch(setAiFeatureAreas(aiFeatureAreas));
  };
};

// free-text prompt typed in the custom prompt block (AIResultCustomPrompt).
// Empty => the "regenerate" button of that block stays disabled.
export const setLookAICustomPrompt = (lookAICustomPrompt) => ({
  type: types.SET_LOOK_AI_CUSTOM_PROMPT,
  payload: {lookAICustomPrompt}
});

// what the custom prompt block (AIResultCustomPrompt) is currently editing:
// {resultId, pieceIndex, dataUrl, viewOnly, regenerateError} - or null when nothing is opened.
// pieceIndex: the index of the opened piece of a multiImage result (2x2), or null when the
// WHOLE result image is being edited - every other kind of result (see WHOLE_IMAGE_PIECE_INDEX
// in LookAIImageOverlayImageContent).
// viewOnly: true when the user clicked a multiImage piece itself (zoom + before/after slider),
// false when they clicked an "edit" button (custom prompt block, no slider). A whole-image edit
// is always viewOnly: false.
// dataUrl is the cut piece produced by cutImageIntoFourPieces (or the result image itself for a
// whole-image edit) - it is what gets posted as b64Image on regenerate (see
// postRegenerateLookAiImage in aiApi/aiRegenerateApi.ts), and it is replaced by the regenerated
// image url once that request succeeds.
export const setLookAIEditPiece = (lookAIEditPiece) => ({
  type: types.SET_LOOK_AI_EDIT_PIECE,
  payload: {lookAIEditPiece}
});

export const setGlobalSettingsRequested = (visualizerType) => {
  return (dispatch, getState) => {
    dispatch({
      type: types.SET_AI_GLOBAL_SETTINGS_REQUESTED,
      payload: {
        aiGlobalSettingsRequested: [...getState().aiState.aiGlobalSettingsRequested, visualizerType],
      }
    })
  }
};
